import { useCallback, useMemo } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { updatePreferenceOverride } from 'app-components/flux/preferences-v2/preferencesV2Actions';
import { getPreferenceObj, getPreferenceValue } from 'app-components/flux/preferences-v2/preferencesV2Selectors';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';
import { tracker } from 'app-components/utils/core';

import { equalSplitFieldDefaults, SPLIT_FIELD_DEFAULTS, type SplitFieldDefaults } from './splitsHelpers';

export const SPLITS_ALWAYS_SHOW_PREF_NAME = 'splitsAlwaysShowFields';

export const SPLITS_ALWAYS_SHOW_FIELD_LABELS: Readonly<Record<keyof SplitFieldDefaults, string>> = {
  tags: 'Tags',
  exclusions: 'Exclusions',
  note: 'Note',
};

export const SPLITS_ALWAYS_SHOW_ORDER: ReadonlyArray<keyof SplitFieldDefaults> = ['tags', 'exclusions', 'note'];

export type SplitsAlwaysShowApi = {
  /** Gated by the `splitsAlwaysShow` LD flag; UI must hide the menu section when `false`. */
  enabled: boolean;
  /** Falls back to all-`false` when `enabled` is `false`, so a stale prefv2 value never leaks through. */
  defaults: SplitFieldDefaults;
  /** No-op when `enabled` is `false` or value is unchanged; tracks one event per flipped flag. */
  commit: (next: SplitFieldDefaults) => void;
};

const normalizeDefaults = (value: unknown): SplitFieldDefaults => {
  const obj = (value && typeof value === 'object' ? value : {}) as Partial<SplitFieldDefaults>;
  return {
    tags: obj.tags === true,
    note: obj.note === true,
    exclusions: obj.exclusions === true,
  };
};

export const useSplitsAlwaysShow = (): SplitsAlwaysShowApi => {
  const dispatch = useAppDispatch();
  const enabled = useAppSelector((state) => Boolean(featureFlagsSelectors.getFeatureFlag(state, 'splitsAlwaysShow')));
  const preference = useAppSelector((state) => getPreferenceObj(state, SPLITS_ALWAYS_SHOW_PREF_NAME));
  const rawValue = useAppSelector((state) =>
    getPreferenceValue(state, SPLITS_ALWAYS_SHOW_PREF_NAME, SPLIT_FIELD_DEFAULTS),
  );

  const defaults = useMemo(() => (enabled ? normalizeDefaults(rawValue) : SPLIT_FIELD_DEFAULTS), [enabled, rawValue]);

  const commit = useCallback(
    (next: SplitFieldDefaults): void => {
      if (!enabled) return;
      if (equalSplitFieldDefaults(next, defaults)) return;

      dispatch(
        updatePreferenceOverride({
          id: SPLITS_ALWAYS_SHOW_PREF_NAME,
          preferenceName: SPLITS_ALWAYS_SHOW_PREF_NAME,
          preferenceId: preference?.preferenceId,
          dataType: 'JSON',
          value: next,
        }),
      );

      SPLITS_ALWAYS_SHOW_ORDER.forEach((key) => {
        if (next[key] !== defaults[key]) {
          tracker.track(tracker.events.selectSplitsAlways, {
            toggle: next[key] ? 'on' : 'off',
            attribute: key,
          });
        }
      });
    },
    [defaults, dispatch, enabled, preference?.preferenceId],
  );

  return useMemo(() => ({ enabled, defaults, commit }), [defaults, commit, enabled]);
};
